import type { CSSProperties, HTMLAttributes } from "react";
import { Kicker } from "../../ui/v2/Kicker";
import { scaledFont } from "@/styles/scaledFont";

export interface LeanPassProps extends HTMLAttributes<HTMLDivElement> {
  /** The left kicker — `t('…')`, never a literal. */
  leanTitle: string;
  /** What to lean on: one short lowercase value. */
  lean: string;
  /** The right kicker — `t('…')`, never a literal. */
  passTitle: string;
  /** What to let pass: one short lowercase value. */
  pass: string;
}

const BLOCK: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
};

const COLUMN: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

/* `500 14px/20px` is the block's own size — one step under `--t-read` and a
 * step over `--t-caption`; the values are phrases, not sentences. */
const VALUE: CSSProperties = {
  font: scaledFont("500 14px/20px var(--font-body)"),
  color: "var(--d-fg-2)",
};

/**
 * "Lean into / let pass" — the day's two instructions, side by side. The copy
 * register is the caller's: short, lowercase, no full stop.
 *
 * Ported from `LeanPass` in
 * `docs/design/handoff/design/components/today/LeanPass.jsx`.
 */
export function LeanPass({
  leanTitle,
  lean,
  passTitle,
  pass,
  className,
  style,
  ...rest
}: LeanPassProps): JSX.Element {
  return (
    <div className={className} style={{ ...BLOCK, ...style }} {...rest}>
      {[
        { title: leanTitle, value: lean },
        { title: passTitle, value: pass },
      ].map((column) => (
        <div key={column.title} data-lean-col style={COLUMN}>
          <Kicker>{column.title}</Kicker>
          <span style={VALUE}>{column.value}</span>
        </div>
      ))}
    </div>
  );
}

export default LeanPass;
